import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Snackbar,
  Alert,
  Typography,
  Container,
  Divider,
  Paper,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../../context/AuthContext';
import { axiosClient } from '../../../services/axiosClient';
import { Navigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';

const Subscribe = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [hasActive, setHasActive] = useState(false);
  const [pending, setPending] = useState([]);
  const [activeSubData, setActiveSubData] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [eligibleForTrial, setEligibleForTrial] = useState(false);

  const featureLabels = {
    'max_salons': t('pro_subscription.features.max_salons'),
    'access_clients': t('pro_subscription.features.access_clients'),
    'access_dashboard_global': t('pro_subscription.features.access_dashboard_global'),
    'access_dashboard_per_salon': t('pro_subscription.features.access_dashboard_per_salon'),
    'manage_clients': t('pro_subscription.features.manage_clients'),
    'access_messaging': t('pro_subscription.features.access_messaging'),
    'basic_support': t('pro_subscription.features.basic_support'),
    'premium_support': t('pro_subscription.features.premium_support'),
    'analytics': t('pro_subscription.features.analytics'),
    'custom_domain': t('pro_subscription.features.custom_domain'),
    'unlimited_services': t('pro_subscription.features.unlimited_services'),
    'unlimited_staff': t('pro_subscription.features.unlimited_staff'),
    'unlimited_appointments': t('pro_subscription.features.unlimited_appointments'),
    'email_notifications': t('pro_subscription.features.email_notifications'),
    'sms_notifications': t('pro_subscription.features.sms_notifications'),
    'online_booking': t('pro_subscription.features.online_booking'),
    'calendar_sync': t('pro_subscription.features.calendar_sync'),
    'inventory_management': t('pro_subscription.features.inventory_management'),
    'loyalty_program': t('pro_subscription.features.loyalty_program'),
    'marketing_tools': t('pro_subscription.features.marketing_tools'),
    'custom_reports': t('pro_subscription.features.custom_reports'),
    'notifications_clients': t('pro_subscription.features.notifications_clients'),
    'rdv_en_ligne': t('pro_subscription.features.rdv_en_ligne'),
    'gestion_equipe': t('pro_subscription.features.gestion_equipe'),
    'statistiques_salon': t('pro_subscription.features.statistiques_salon')
  };

  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });
  const closeToast = (_, reason) => { if (reason !== 'clickaway') setToast({ ...toast, open: false }); };

  // Configure dayjs locale based on current language
  useEffect(() => {
    const localeMap = {
      'fr': 'fr',
      'ar': 'ar', 
      'en': 'en'
    };
    dayjs.locale(localeMap[i18n.language] || 'en');
  }, [i18n.language]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        console.log('Fetching subscription data...');
        const [statusRes, plansRes] = await Promise.all([
          axiosClient.get('/subscriptions/my/'),
          axiosClient.get('/subscriptions/plans/'),
        ]);

        if (cancelled) return;

        console.log('Subscription status response:', statusRes?.data);
        console.log('Plans response:', plansRes?.data);

        setHasActive(!!statusRes?.data?.has_active);
        setEligibleForTrial(!!statusRes?.data?.eligible_for_trial);
        const pendingSubs = Array.isArray(statusRes?.data?.pending) ? statusRes.data.pending : [];
        setPending(pendingSubs);

        const plansData = Array.isArray(plansRes?.data) ? plansRes.data : [];
        setPlans(plansData);
        setActiveSubData(statusRes?.data?.active || null);
      } catch (e) {
        if (!cancelled) {
          setPlans([]);
          setHasActive(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Déplacer tous les Hooks avant tout retour conditionnel
  const isPro = useMemo(() => user?.role === 'professional_responsable', [user]);

  const alreadyRequestedPlanCodes = useMemo(() => {
    return new Set(pending.map(p => p.plan?.code || p));
  }, [pending]);

  const hasPendingTrial = useMemo(() => {
    const codeSet = new Set(pending.map(p => (p.plan?.code || p || '').toString().toLowerCase()));
    return codeSet.has('essaie') || codeSet.has('trial');
  }, [pending]);

  // Trier les plans par prix croissant et par code pour une cohérence
  const sortedPlans = useMemo(() => {
    return [...plans]
      .filter(p => p.code !== 'essaie' && p.code !== 'trial')
      .sort((a, b) => {
        const priceA = parseFloat(a.price || 0);
        const priceB = parseFloat(b.price || 0);

        // Trier d'abord par prix
        if (priceA !== priceB) {
          return priceA - priceB;
        }

        // Si les prix sont égaux, trier par code
        return (a.code || '').localeCompare(b.code || '');
      });
  }, [plans]);

  const pendingSubscription = useMemo(() => {
    if (!Array.isArray(pending) || pending.length === 0) return null;
    const first = pending[0];

    const codeRaw =
      first?.plan?.code ??
      first?.plan_code ??
      first?.code ??
      first?.plan ??
      first;

    const code = codeRaw != null ? String(codeRaw).toLowerCase() : '';
    const planFromList = sortedPlans.find(p => String(p?.code || '').toLowerCase() === code) || null;

    const plan = planFromList || first?.plan || null;
    const statusValue = first?.status || first?.subscription?.status || 'en_attente';

    return {
      code,
      plan,
      status: statusValue,
    };
  }, [pending, sortedPlans]);

  const activeSubscription = useMemo(() => {
    // We already fetch it in run(), we just need to use it
    return plans.find(p => p.code === activeSubData?.plan?.code) || activeSubData?.plan || null;
  }, [plans, activeSubData]);

  const trialPlan = useMemo(() => {
    return plans.find(p => String(p.code || '').toLowerCase() === 'essaie')
      || plans.find(p => String(p.code || '').toLowerCase() === 'trial')
      || null;
  }, [plans]);

  // Vérifications de redirection après les Hooks
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'professional_responsable') return <Navigate to="/pro" replace />;

  const requestPlan = async (code) => {
    try {
      const res = await axiosClient.post('/subscriptions/request/', { plan_code: code });
      const isTrial = String(code || '').toLowerCase() === 'essaie' || String(code || '').toLowerCase() === 'trial';
      if (isTrial && (res?.data?.subscription?.status === 'active' || res?.status === 201)) {
        showToast(t('pro_subscription.toast.trial_activated'));
      } else {
        showToast(t('pro_subscription.toast.request_sent'));
      }
      const statusRes = await axiosClient.get('/subscriptions/my/');
      setHasActive(!!statusRes?.data?.has_active);
      setActiveSubData(statusRes?.data?.active || null);
      setPending(statusRes?.data?.pending || []);
      setEligibleForTrial(!!statusRes?.data?.eligible_for_trial);
    } catch (e) {
      showToast(e?.response?.data?.detail || t('pro_subscription.toast.request_error'), 'error');
    }
  };

  // Composant PlanCard pour afficher chaque carte d'abonnement
  const PlanCard = ({ plan, requested, onRequest, allPlans }) => {
    const features = plan.features || [];

    const sortedAllPlans = useMemo(() => {
      return [...(allPlans || [])].sort((a, b) => parseFloat(a?.price || 0) - parseFloat(b?.price || 0));
    }, [allPlans]);

    const tierIndex = useMemo(() => {
      return Math.max(0, sortedAllPlans.findIndex(p => p?.id === plan?.id));
    }, [sortedAllPlans, plan?.id]);

    const tierTheme = useMemo(() => {
      const themes = [
        {
          accent: '#2E7D32',
          accentText: '#2E7D32',
          bg: 'linear-gradient(145deg, #f1f9f1, #e0f5e0)',
          border: 'rgba(76, 175, 80, 0.7)',
          hoverShadow: 'rgba(76, 175, 80, 0.25)',
          cardShadow: 'rgba(0, 0, 0, 0.05)',
          topBar: 'linear-gradient(90deg, #4CAF50, #2E7D32)',
          icon: '#2E7D32',
          iconBg: 'rgba(76, 175, 80, 0.15)',
          featureBgCommon: 'rgba(76, 175, 80, 0.14)',
          featureBgUnique: 'rgba(76, 175, 80, 0.08)',
          featureBorderCommon: 'rgba(76, 175, 80, 0.35)',
          featureBorderUnique: 'rgba(76, 175, 80, 0.22)',
          chipBg: 'rgba(76, 175, 80, 0.2)',
          chipText: '#1b5e20',
          chipBorder: '#4caf50',
          buttonBg: 'linear-gradient(45deg, #4CAF50 0%, #2E7D32 100%)',
          buttonText: 'white',
          buttonShadow: 'rgba(76, 175, 80, 0.4)',
        },
        {
          accent: '#1565C0',
          accentText: '#1565C0',
          bg: 'linear-gradient(145deg, #eaf2ff, #dbeaff)',
          border: 'rgba(33, 150, 243, 0.7)',
          hoverShadow: 'rgba(33, 150, 243, 0.25)',
          cardShadow: 'rgba(0, 0, 0, 0.05)',
          topBar: 'linear-gradient(90deg, #2196F3, #1565C0)',
          icon: '#1565C0',
          iconBg: 'rgba(33, 150, 243, 0.15)',
          featureBgCommon: 'rgba(33, 150, 243, 0.14)',
          featureBgUnique: 'rgba(33, 150, 243, 0.08)',
          featureBorderCommon: 'rgba(33, 150, 243, 0.35)',
          featureBorderUnique: 'rgba(33, 150, 243, 0.22)',
          chipBg: 'rgba(33, 150, 243, 0.2)',
          chipText: '#0d47a1',
          chipBorder: '#2196F3',
          buttonBg: 'linear-gradient(45deg, #2196F3 0%, #1565C0 100%)',
          buttonText: 'white',
          buttonShadow: 'rgba(33, 150, 243, 0.4)',
        },
        {
          accent: '#b8860b',
          accentText: '#b8860b',
          bg: 'linear-gradient(145deg, #fff9e6, #fff3cc)',
          border: 'rgba(255, 215, 0, 0.7)',
          hoverShadow: 'rgba(255, 215, 0, 0.25)',
          cardShadow: 'rgba(255, 215, 0, 0.15)',
          topBar: 'linear-gradient(90deg, #ffd700, #ffa500)',
          icon: '#d4af37',
          iconBg: 'rgba(255, 215, 0, 0.15)',
          featureBgCommon: 'rgba(255, 215, 0, 0.14)',
          featureBgUnique: 'rgba(255, 215, 0, 0.08)',
          featureBorderCommon: 'rgba(255, 215, 0, 0.35)',
          featureBorderUnique: 'rgba(255, 215, 0, 0.22)',
          chipBg: 'rgba(255, 215, 0, 0.2)',
          chipText: '#8d6e00',
          chipBorder: '#ffd700',
          buttonBg: 'linear-gradient(45deg, #ffd700 0%, #ffb700 100%)',
          buttonText: '#5d4037',
          buttonShadow: 'rgba(255, 215, 0, 0.4)',
        },
      ];

      const idx = tierIndex % themes.length;
      return themes[idx];
    }, [tierIndex]);

    const isHighlighted = useMemo(() => {
      if (!sortedAllPlans || sortedAllPlans.length === 0) return false;
      const index = sortedAllPlans.findIndex(p => p?.id === plan?.id);
      return index === sortedAllPlans.length - 1;
    }, [sortedAllPlans, plan?.id]);

    const comparisonPlan = useMemo(() => {
      if (!sortedAllPlans || sortedAllPlans.length === 0) return null;
      if (sortedAllPlans.length === 1) return null;
      if (sortedAllPlans.length === 2) {
        return sortedAllPlans[0]?.id === plan?.id ? sortedAllPlans[1] : sortedAllPlans[0];
      }

      const index = sortedAllPlans.findIndex(p => p?.id === plan?.id);
      if (index > 0) return sortedAllPlans[index - 1];
      if (index === 0 && sortedAllPlans.length > 1) return sortedAllPlans[1];
      return null;
    }, [sortedAllPlans, plan?.id]);

    const baseOrderIndex = useMemo(() => {
      const base = sortedAllPlans?.[0]?.features || [];
      const idx = new Map();
      base.forEach((f, i) => {
        if (f?.key) idx.set(f.key, i);
      });
      return idx;
    }, [sortedAllPlans]);

    // Trouver les fonctionnalités communes à tous les plans
    const commonFeatures = useMemo(() => {
      if (!allPlans || allPlans.length === 0) return new Set();

      // Prendre les clés du premier plan comme base
      const firstPlanFeatures = new Set(allPlans[0].features?.map(f => f.key) || []);

      // Garder seulement les fonctionnalités présentes dans tous les plans
      allPlans.slice(1).forEach(p => {
        const planFeatures = new Set(p.features?.map(f => f.key) || []);
        for (const key of firstPlanFeatures) {
          if (!planFeatures.has(key)) {
            firstPlanFeatures.delete(key);
          }
        }
      });

      return firstPlanFeatures;
    }, [allPlans]);

    // Séparer les fonctionnalités (dyn: intersection true avec le plan de comparaison + uniques true)
    const [commonFeaturesList, uniqueFeaturesList] = useMemo(() => {
      const common = [];
      const unique = [];

      const sortByBaseOrder = (arr) => {
        return [...arr].sort((a, b) => {
          const ia = baseOrderIndex.has(a.key) ? baseOrderIndex.get(a.key) : Number.MAX_SAFE_INTEGER;
          const ib = baseOrderIndex.has(b.key) ? baseOrderIndex.get(b.key) : Number.MAX_SAFE_INTEGER;
          if (ia !== ib) return ia - ib;
          return String(a.key || '').localeCompare(String(b.key || ''));
        });
      };

      if (comparisonPlan) {
        const thisTrue = new Set(features.filter(f => f?.value === true).map(f => f.key));
        const otherTrue = new Set((comparisonPlan.features || []).filter(f => f?.value === true).map(f => f.key));

        const commonKeys = new Set();
        for (const key of thisTrue) {
          if (otherTrue.has(key)) commonKeys.add(key);
        }

        features.forEach((f) => {
          if (f?.value !== true) return;
          if (commonKeys.has(f.key)) common.push(f);
          else unique.push(f);
        });

        return [sortByBaseOrder(common), sortByBaseOrder(unique)];
      }

      // Fallback (autres plans): logique existante (communes à tous vs uniques)
      features.forEach(feature => {
        if (commonFeatures.has(feature.key)) common.push(feature);
        else unique.push(feature);
      });

      return [common, unique];
    }, [features, commonFeatures, baseOrderIndex, comparisonPlan]);

    // Fonction pour obtenir le libellé d'une fonctionnalité
    const getFeatureLabel = (feature) => {
      if (typeof feature === 'object' && feature !== null) {
        return featureLabels[feature.key] || feature.key;
      }
      return featureLabels[feature] || feature;
    };

    // Fonction pour rendre une fonctionnalité
    const renderFeatureItem = (feature, isCommon = true) => (
      <ListItem
        key={feature.key}
        className="feature-item"
        sx={{
          py: 1.5,
          px: 2,
          mx: 1,
          borderRadius: '8px',
          backgroundImage: `linear-gradient(135deg, ${isCommon ? tierTheme.featureBgCommon : tierTheme.featureBgUnique
            } 0%, rgba(255,255,255,0.35) 50%, ${isCommon ? tierTheme.featureBgCommon : tierTheme.featureBgUnique
            } 100%)`,
          backgroundSize: '200% 200%',
          backgroundPosition: '0% 50%',
          border: `1px solid ${isCommon ? tierTheme.featureBorderCommon : tierTheme.featureBorderUnique}`,
          '&:not(:last-child)': {
            mb: 0.5,
          },
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundPosition: '100% 50%',
          },
          opacity: isCommon ? 1 : 0.8,
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: tierTheme.iconBg,
                transform: 'scale(0.8)',
                transition: 'all 0.3s ease',
              },
              '&:hover::before': {
                transform: 'scale(1.2)',
                opacity: 0.8,
              }
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                color: tierTheme.icon,
                fontSize: '1.2rem',
                position: 'relative',
                zIndex: 1,
              }}
            />
          </Box>
        </ListItemIcon>
        <ListItemText
          primary={getFeatureLabel(feature)}
          primaryTypographyProps={{
            variant: 'body2',
            sx: {
              fontWeight: 600,
              color: '#1a1a1a',
              fontSize: '0.95rem',
              lineHeight: 1.4,
            },
          }}
        />
      </ListItem>
    );

    const isActive = plan.code === activeSubData?.plan?.code;

    return (
      <Fade in={true} timeout={500}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-10px) scale(1.02)',
              boxShadow: `0 20px 40px ${tierTheme.hoverShadow}`,
              backgroundPosition: '100% 50%',
            },
            backgroundImage: tierTheme.bg,
            backgroundSize: '200% 200%',
            backgroundPosition: '0% 50%',
            border: isActive ? `3px solid ${theme.palette.primary.main}` : `2px solid ${tierTheme.border}`,
            borderRadius: '16px',
            overflow: 'hidden',
            position: 'relative',
            maxWidth: 400,
            mx: 'auto',
            boxShadow: isActive ? `0 15px 35px ${theme.palette.primary.light}40` : `0 10px 30px ${tierTheme.cardShadow}`,
            '&::before': {
              content: isActive ? `"${t('pro_subscription.current_plan_label')}"` : '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: '0.7rem',
              fontWeight: 900,
              color: 'white',
              height: isActive ? '20px' : '4px',
              paddingTop: isActive ? '2px' : 0,
              background: isActive ? theme.palette.primary.main : tierTheme.topBar,
              backgroundSize: '200% 200%',
              backgroundPosition: '0% 50%',
              opacity: 0.8,
              transition: 'all 0.4s ease',
              zIndex: 10
            },
            '&:hover::before': {
              height: isActive ? '24px' : '6px',
              opacity: 1,
              backgroundPosition: '100% 50%',
            },
            '&:active': {
              transform: 'translateY(-5px) scale(0.99)',
            },
          }}
        >
          <CardHeader
            title={
              <Box
                sx={{
                  textAlign: 'center',
                  mt: isHighlighted ? 3 : 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 800,
                    color: tierTheme.accentText,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    mb: 1
                  }}
                >
                  {plan.name}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    color: tierTheme.accentText,
                    lineHeight: 1,
                    mb: 1
                  }}
                >
                  {parseFloat(plan.price).toLocaleString('fr-DZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} DA
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '1rem',
                      color: 'text.secondary',
                      ml: 1,
                      fontWeight: 400
                    }}
                  >
                    {t('pro_subscription.per_month')}
                  </Typography>
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Chip
                      label={(() => {
                        const maxSalonsFeature = plan.features?.find(f => f.key === 'max_salons');
                        const maxSalons = maxSalonsFeature?.value;
                        return maxSalons ? `${maxSalons} ${maxSalons > 1 ? t('pro_subscription.salons_plural') : t('pro_subscription.salon_singular')} ${t('pro_subscription.included')}` : t('pro_subscription.multiple_salons');
                      })()}
                      color="default"
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        px: 1.5,
                        py: 0.8,
                        backgroundImage: `linear-gradient(135deg, ${tierTheme.chipBg} 0%, rgba(255,255,255,0.5) 50%, ${tierTheme.chipBg} 100%)`,
                        backgroundSize: '200% 200%',
                        backgroundPosition: '0% 50%',
                        color: tierTheme.chipText,
                        border: `1px solid ${tierTheme.chipBorder}`,
                        transition: 'background-position 0.35s ease',
                        '&:hover': {
                          backgroundPosition: '100% 50%',
                        },
                        '& .MuiChip-label': {
                          px: 0.5
                        }
                      }}
                    />
                  </Box>
                </Typography>
                {plan.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      fontSize: '0.9rem'
                    }}
                  >
                    {plan.description}
                  </Typography>
                )}
              </Box>
            }
            sx={{
              pb: 0,
              '& .MuiCardHeader-content': {
                width: '100%'
              }
            }}
          />

          <CardContent sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            pt: 2,
            '&:hover .feature-item': {
              transform: 'translateX(5px)',
              '&:nth-of-type(odd)': {
                transform: 'translateX(8px)',
              }
            }
          }}>
            {/* Afficher les fonctionnalités communes en ligne */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mb: 2,
              px: 2,
            }}>
              <List sx={{ width: '100%', py: 0 }}>
                {commonFeaturesList
                  .filter(feature => feature.value === true)
                  .map(feature => renderFeatureItem(feature, true))}
              </List>
            </Box>

            {/* Afficher les fonctionnalités uniques en dessous */}
            <List sx={{
              width: '100%',
              py: 0,
              '& .MuiListItem-root': {
                transition: 'all 0.3s ease, background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  borderRadius: '8px',
                  transform: 'translateX(4px)',
                }
              },
              '& .MuiListItemButton-root': {
                borderRadius: '8px',
                px: 1.5,
                py: 0.5,
                my: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.02)',
                }
              }
            }}>
              {uniqueFeaturesList.length > 0 && (
                <>
                  <Divider sx={{ my: 1, mx: 2, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('pro_subscription.exclusive_features')}
                    </Typography>
                  </Divider>
                  {uniqueFeaturesList
                    .filter(feature => feature.value === true)
                    .map((feature, index) => (
                      <React.Fragment key={index}>
                        {renderFeatureItem(feature, false)}
                      </React.Fragment>
                    ))}
                </>
              )}
            </List>

            <Box sx={{ mt: 'auto', pt: 3, textAlign: 'center' }}>
              {isActive ? (
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  disabled
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 800,
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                    color: theme.palette.primary.main,
                    textTransform: 'none'
                  }}
                >
                  {t('pro_subscription.your_current_plan')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={requested}
                  onClick={() => onRequest(plan.code)}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    fontSize: '1rem',
                    backgroundImage: tierTheme.buttonBg,
                    backgroundSize: '200% 200%',
                    backgroundPosition: '0% 50%',
                    color: tierTheme.buttonText,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${tierTheme.buttonShadow}`,
                      backgroundPosition: '100% 50%',
                    },
                    transition: 'all 0.35s ease, background-position 0.35s ease',
                  }}
                >
                  {requested ? t('pro_subscription.request_sent_label') : t('pro_subscription.change_plan')}
                  {isHighlighted && !requested && (
                    <StarIcon sx={{ ml: 1, fontSize: '1.2rem' }} />
                  )}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', textAlign: 'center', mb: 1 }}>
          {t('pro_subscription.page_title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          {t('pro_subscription.page_subtitle')}
        </Typography>
      </Box>

      {(!hasActive && eligibleForTrial && !hasPendingTrial && trialPlan) && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 6,
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900 }}>{t('pro_subscription.trial_title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('pro_subscription.trial_subtitle')}</Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={() => requestPlan(trialPlan.code)}
            sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 2 }}
          >
            {t('pro_subscription.trial_button')}
          </Button>
        </Paper>
      )}

      {
        activeSubData && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              mb: 8,
              background: 'linear-gradient(135deg, #f8faff 0%, #eff6ff 100%)',
              border: `1px solid ${theme.palette.primary.light}`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Chip
                    label={t('pro_subscription.active_sub_chip')}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 800, mb: 2, borderRadius: 1.5 }}
                  />
                  <Typography variant="h3" sx={{ fontWeight: 900, color: theme.palette.primary.dark, mb: 1 }}>
                    {activeSubData.plan?.name || t('pro_subscription.active_plan_fallback')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    {t('pro_subscription.active_since', { date: dayjs(activeSubData.start_date).format('D MMMM YYYY') })}
                  </Typography>
                  {activeSubData.end_date && (
                    <Typography variant="body2" sx={{ color: theme.palette.error.main, mt: 1, fontWeight: 700 }}>
                      {t('pro_subscription.ends_on', { date: dayjs(activeSubData.end_date).format('D MMMM YYYY') })}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: theme.palette.primary.main }}>
                    {parseFloat(activeSubData.plan?.price || 0).toLocaleString('fr-DZ')} DA
                    <Typography component="span" variant="body1" sx={{ color: 'text.secondary', ml: 1 }}>
                      {t('pro_subscription.per_month')}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 800, mt: 1 }}>
                    ✓ {t('pro_subscription.payment_up_to_date')}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                right: -20,
                bottom: -20,
                opacity: 0.05,
                transform: 'rotate(-15deg)'
              }}
            >
              <StarIcon sx={{ fontSize: 200 }} />
            </Box>
          </Paper>
        )
      }

      {
        pendingSubscription && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              mb: 6,
              backgroundColor: '#fff9eb',
              border: '1px solid #ffeeba',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box sx={{ backgroundColor: '#fff', borderRadius: '50%', p: 1, display: 'flex' }}>
              <CircularProgress size={24} thickness={5} color="warning" />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 800, color: '#856404' }}>
                {t('pro_subscription.pending_title')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#856404', opacity: 0.8 }}>
                {t('pro_subscription.pending_desc', { plan: pendingSubscription.plan?.name || pendingSubscription.code })}
              </Typography>
            </Box>
            <Chip label={t('pro_subscription.pending_chip')} size="small" variant="outlined" sx={{ fontWeight: 900, color: '#856404', borderColor: '#856404' }} />
          </Paper>
        )
      }

      {
        loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, fontWeight: 700 }}>{t('pro_subscription.loading')}</Typography>
          </Box>
        ) : (
          <Grid container spacing={4} justifyContent="center">
            {sortedPlans.length === 0 ? (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                  <Typography color="text.secondary">{t('pro_subscription.no_plans')}</Typography>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                    sx={{ mt: 2 }}
                  >
                    {t('common.retry')}
                  </Button>
                </Paper>
              </Grid>
            ) : (
              sortedPlans.map((plan) => {
                const code = String(plan.code || '').toLowerCase();
                const requested = alreadyRequestedPlanCodes.has(code);
                return (
                  <Grid key={plan.id} item xs={12} md={6} lg={4}>
                    <PlanCard
                      plan={plan}
                      requested={requested}
                      onRequest={requestPlan}
                      allPlans={sortedPlans}
                    />
                  </Grid>
                );
              })
            )}
          </Grid>
        )
      }

      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('pro_subscription.custom_solution')}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          href="/contact"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: '0.5px'
          }}
        >
          {t('pro_subscription.contact_us')}
        </Button>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container >
  );
};

export default Subscribe;
